import React, { useEffect, useRef, useState } from 'react';
import TooltipSlider from 'src/features/common/components/tool-tip-slider/tool-tip-slider';
import { CardFeeDetailsV2DTO, CardFeesData, IFeesService } from 'src/features/common';
import useDebounce from 'src/hooks/use-debounce';
import { CP } from '../../helpers/fee-slider-devices';
import { DotLoader } from 'src/components/dot-loader/dot-loader';
import FeesTable from '../fees-table/fees-table';
import SpecialRates from '../special-rates/special-rates';
import RevampToolTip from '../revamp-tooltip/revamp-tooltip';
import './monthly-turn-over.scss';

interface MonthlyTurnOverProps {
  feesService: IFeesService;
  selectedDeviceNames: string[];
  defaultTurnover: number;
}

const MonthlyTurnOver = ({
  feesService,
  selectedDeviceNames,
  defaultTurnover,
}: MonthlyTurnOverProps) => {
  const REQUEST_DELAY = 500;

  const [cardFees, setCardFees] = useState<CardFeeDetailsV2DTO>();
  const [turnOverAmount, setTurnOverAmount] = useState<number>(defaultTurnover);
  const [estimatedFees, setEstimatedFees] = useState<CardFeesData[]>();
  const [tableView, setTableView] = useState<string>(CP);
  const [tierType, setTierType] = useState<'Variable' | 'Fixed'>('Fixed');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tooltipParent = useRef<HTMLDivElement>(null);

  const turnOverAmountDebounced = useDebounce(turnOverAmount, REQUEST_DELAY);
  const marks = {
    1: 'R1K',
    40: 'R40K',
    80: 'R80K',
    120: 'R120K',
    160: 'R160K',
    200: 'R20K',
    240: '>R240K',
  };

  const changeTableView = (newView) => {
    if (tableView === newView) return;
    setTableView(newView);
  };

  const tipFormat = (value) => {
    if (value === 0) return 'R0';
    return `R${value} 000`;
  };

  const updateFees = (value: number | number[]) => {
    if (typeof value === 'number') {
      const amount = value * 1000;
      setTurnOverAmount(amount);
    }
  };

  const isFeeDataEqual = (a: CardFeesData, b: CardFeesData): boolean => {
    return (
      a.product_name === b.product_name &&
      a.pricing_tier === b.pricing_tier &&
      a.card_type === b.card_type
    );
  };

  const onlyUniqueFeeData = (data: CardFeeDetailsV2DTO): CardFeeDetailsV2DTO => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const uniqueArr: CardFeesData[] = [];
      value.forEach((obj) => {
        if (!uniqueArr.some((item: CardFeesData) => isFeeDataEqual(item, obj))) {
          uniqueArr.push(obj);
        }
      });
      acc[key] = uniqueArr;
      return acc;
    }, {});
  };

  const formatResponse = (data: CardFeeDetailsV2DTO): CardFeeDetailsV2DTO => {
    const categories = ['Card Not Present', 'Card Present'];
    return Object.entries(data).reduce((acc, [, value]) => {
      for (const cat of categories) {
        acc[cat] = (acc[cat] || []).concat(value.filter((item) => item.fee_category === cat) || []);
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    const abortController = new AbortController();
    const fetchCardFees = async () => {
      try {
        const result = await feesService.getCardFeesV2(
          selectedDeviceNames,
          turnOverAmount,
          undefined,
          abortController,
        );

        if (result) {
          setIsLoading(abortController.signal.aborted ? true : false);
          const formattedResult = formatResponse(result);
          setCardFees(onlyUniqueFeeData(formattedResult));
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (turnOverAmount < 250000) {
      setIsLoading(true);
      fetchCardFees();

      return () => {
        abortController.abort();
      };
    }
  }, [turnOverAmountDebounced]);

  useEffect(() => {
    if (cardFees && Object.values(cardFees).length > 0) {
      setTierType(
        cardFees[tableView].some((item) => item.pricing_tier === 'Variable') ? 'Variable' : 'Fixed',
      );
      setEstimatedFees(cardFees[tableView]);
    }
  }, [cardFees, tableView]);

  return (
    <div className="monthly-turn-over" ref={tooltipParent}>
      <h3 className="monthly-turn-over-title">Select your monthly turnover</h3>

      <div className="slider-container">
        <TooltipSlider
          tipFormatter={tipFormat}
          onChange={updateFees}
          min={1}
          max={250}
          step={1}
          marks={marks}
          dotStyle={{ display: 'none' }}
          visible={turnOverAmount >= 250000 ? true : undefined}
          defaultValue={(defaultTurnover ?? 0) / 1000}
        />
        <p>Your estimated monthly turnover</p>
      </div>

      <div className="estimated-fees-container">
        {turnOverAmount >= 250000 ? (
          <SpecialRates />
        ) : (
          <>
            {isLoading ? (
              <DotLoader />
            ) : (
              <>
                {estimatedFees && estimatedFees?.length > 0 ? (
                  <>
                    <h3 className="monthly-turn-over-title">Your estimated fees</h3>

                    <FeesTable
                      tableView={tableView}
                      estimatedFees={estimatedFees}
                      tierType={tierType}
                      changeTableView={changeTableView}
                    />
                  </>
                ) : (
                  <h3 className="monthly-turn-over-title">No Data</h3>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="read-more-container">
        <RevampToolTip
          content={
            <p>
              A physical payment/card-present <strong>(CP)</strong> transaction occurs when payment
              details are captured in person for a product or service. A virtual
              payment/card-not-present <strong>(CNP)</strong> transaction occurs when neither the
              cardholder nor the credit card is physically present at the time of the transaction.
            </p>
          }
          placement="top-start"
          parentRef={tooltipParent.current}
        >
          <p className="read-more">*More about Physical & Virtual card payments?</p>
        </RevampToolTip>
      </div>
    </div>
  );
};

export default MonthlyTurnOver;
